import Alert, { AlertDescription, AlertTitle } from '../components/ui/Alert';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card, { CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { Checkbox } from '../components/ui/Checkbox';
import CutOut from '../components/ui/CutOut';
import Input from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import Skeleton from '../components/ui/Skeleton';
import { Textarea } from '../components/ui/Textarea';

const display = { fontFamily: "'Permanent Marker', cursive" };
const body = { fontFamily: "'Patrick Hand', cursive" };
const mono = { fontFamily: "'Special Elite', monospace" };

function Section({ title, kicker, children }) {
  return (
    <section className="mb-12">
      <div className="mb-4 border-b-2 border-[#1a1a1a] pb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b5f4d]" style={mono}>
          {kicker}
        </div>
        <h2 className="text-3xl text-[#1a1a1a]" style={display}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Swatch({ name, hex, role, light }) {
  return (
    <div className="flex items-stretch border-2 border-[#1a1a1a] shadow-[3px_3px_0px_#1a1a1a] bg-[#fefcf6]">
      <div className="w-20 shrink-0" style={{ background: hex }} />
      <div className="p-3 flex-1">
        <div className="text-base text-[#1a1a1a]" style={body}>
          {name}
        </div>
        <div className="text-xs text-[#6b5f4d]" style={mono}>
          {hex}
        </div>
        {role && (
          <div
            className={`text-xs mt-1 ${light ? 'text-[#6b5f4d]' : 'text-[#1a1a1a]'}`}
            style={body}
          >
            {role}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs uppercase tracking-wider text-[#6b5f4d]" style={mono}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function StyleSample() {
  return (
    <div className="min-h-screen bg-[#f5f0e8] px-8 py-10" style={body}>
      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="max-w-6xl mx-auto mb-12">
        <div className="text-xs uppercase tracking-[0.25em] text-[#d62828]" style={mono}>
          VotePlan · Style Sample
        </div>
        <h1 className="text-6xl text-[#1a1a1a] leading-none mt-2" style={display}>
          Zine / DIY Protest
        </h1>
        <p className="mt-3 text-lg text-[#1a1a1a] max-w-2xl" style={body}>
          Marker on posterboard. Cut-and-paste. Fight the power. Use this page as a Figma reference
          — every swatch, type style, and component below renders from the live app tokens.
        </p>
      </header>

      <div className="max-w-6xl mx-auto">
        {/* ── 1. COLOR ─────────────────────────────────────────── */}
        <Section kicker="01 · Tokens" title="Color">
          <div className="mb-2 text-sm text-[#6b5f4d]" style={mono}>
            Ink &amp; paper — the bones
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Swatch name="Ink" hex="#1a1a1a" role="text, borders, primary buttons" />
            <Swatch name="Paper" hex="#fefcf6" role="card surface, button text" light />
            <Swatch name="Kraft" hex="#ede5d4" role="page bg, muted surface" light />
            <Swatch name="Kraft-50" hex="#f5f0e8" role="lightest paper" light />
            <Swatch name="Kraft-200" hex="#d6cbb5" role="dividers, low-contrast bg" light />
            <Swatch name="Muted ink" hex="#6b5f4d" role="captions, secondary text" />
          </div>

          <div className="mb-2 text-sm text-[#6b5f4d]" style={mono}>
            Marker — the highlight, the scream
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Swatch name="Marker red" hex="#d62828" role="accent, links, danger" />
            <Swatch name="Highlighter yellow" hex="#fcbf49" role="secondary, callouts" light />
            <Swatch name="Orange marker" hex="#f77f00" role="warning, urgency" light />
            <Swatch name="Red ink dark" hex="#991b1b" role="error hover" />
          </div>

          <div className="mb-2 text-sm text-[#6b5f4d]" style={mono}>
            Status surfaces
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Swatch name="Error wash" hex="#fff0f0" role="destructive alert bg" light />
            <Swatch name="Highlight wash" hex="#fff3b0" role="success/secondary tint" light />
            <Swatch name="Warning wash" hex="#ffe0b3" role="warning tint" light />
          </div>
        </Section>

        {/* ── 2. TYPOGRAPHY ────────────────────────────────────── */}
        <Section kicker="02 · Tokens" title="Typography">
          <div className="space-y-6">
            <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-6 shadow-[3px_3px_0px_#1a1a1a]">
              <div
                className="text-[10px] uppercase tracking-widest text-[#6b5f4d] mb-2"
                style={mono}
              >
                Display · 'Permanent Marker'
              </div>
              <div className="text-6xl text-[#1a1a1a] leading-none" style={display}>
                Vote like your block depends on it
              </div>
              <div className="text-xs text-[#6b5f4d] mt-3" style={mono}>
                Use for: page titles, hero headlines, big numbers. One per screen, max.
              </div>
            </div>

            <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-6 shadow-[3px_3px_0px_#1a1a1a]">
              <div
                className="text-[10px] uppercase tracking-widest text-[#6b5f4d] mb-2"
                style={mono}
              >
                Body · 'Patrick Hand'
              </div>
              <div className="space-y-2">
                <div className="text-3xl text-[#1a1a1a]" style={body}>
                  Heading — 30px
                </div>
                <div className="text-2xl text-[#1a1a1a]" style={body}>
                  Subhead — 24px
                </div>
                <div className="text-lg text-[#1a1a1a]" style={body}>
                  Lead — 18px
                </div>
                <div className="text-base text-[#1a1a1a]" style={body}>
                  Body — 16px. The kraft-paper rises to meet the marker. Every form field,
                  paragraph, button label, and card description lives here.
                </div>
                <div className="text-sm text-[#1a1a1a]" style={body}>
                  Small — 14px. Meta info, helper text, captions in tight spaces.
                </div>
                <div className="text-xs text-[#6b5f4d]" style={body}>
                  Caption — 12px (muted). Use the muted ink color, not for primary content.
                </div>
              </div>
            </div>

            <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-6 shadow-[3px_3px_0px_#1a1a1a]">
              <div
                className="text-[10px] uppercase tracking-widest text-[#6b5f4d] mb-2"
                style={mono}
              >
                Mono · 'Special Elite'
              </div>
              <div className="space-y-2">
                <div className="text-sm uppercase tracking-[0.2em] text-[#1a1a1a]" style={mono}>
                  Eyebrow / Kicker
                </div>
                <div className="text-sm text-[#1a1a1a]" style={mono}>
                  Hex codes, timestamps, dates: #1a1a1a · 2026-05-20 · v1.2.4
                </div>
                <div className="text-xs uppercase tracking-wider text-[#6b5f4d]" style={mono}>
                  Badge / Tag label
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* ── 3. SHADOWS & EDGES ──────────────────────────────── */}
        <Section kicker="03 · Tokens" title="Shadows · Edges · Radius">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-[#fefcf6] border-2 border-[#1a1a1a] p-5 shadow-[3px_3px_0px_#1a1a1a]">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                shadow-sm
              </div>
              <div className="text-lg text-[#1a1a1a]" style={body}>
                3px 3px 0px #1a1a1a
              </div>
              <div className="text-xs text-[#6b5f4d] mt-1" style={body}>
                Default for buttons, inputs, cards.
              </div>
            </div>
            <div className="bg-[#fefcf6] border-2 border-[#1a1a1a] p-5 shadow-[4px_4px_0px_#1a1a1a]">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                shadow-md
              </div>
              <div className="text-lg text-[#1a1a1a]" style={body}>
                4px 4px 0px #1a1a1a
              </div>
              <div className="text-xs text-[#6b5f4d] mt-1" style={body}>
                Hover state. The card "rises."
              </div>
            </div>
            <div className="bg-[#fefcf6] border-2 border-[#1a1a1a] p-5 shadow-[6px_6px_0px_#1a1a1a]">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                shadow-lg
              </div>
              <div className="text-lg text-[#1a1a1a]" style={body}>
                6px 6px 0px #1a1a1a
              </div>
              <div className="text-xs text-[#6b5f4d] mt-1" style={body}>
                Modals, prominent cards.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-5 shadow-[3px_3px_0px_#d62828]">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                Focus / error shadow
              </div>
              <div className="text-lg text-[#d62828]" style={body}>
                3px 3px 0px #d62828
              </div>
            </div>
            <div className="border-2 border-dashed border-[#1a1a1a] bg-[#fefcf6] p-5">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                Dashed border
              </div>
              <div className="text-lg text-[#1a1a1a]" style={body}>
                Outline buttons hover, ghost states, sponsored cards.
              </div>
            </div>
            <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-5">
              <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-1" style={mono}>
                Radius
              </div>
              <div className="text-3xl text-[#1a1a1a]" style={display}>
                0px
              </div>
              <div className="text-xs text-[#6b5f4d] mt-1" style={body}>
                Scissors don&apos;t make curves.
              </div>
            </div>
          </div>
        </Section>

        {/* ── 4. BUTTONS ──────────────────────────────────────── */}
        <Section kicker="04 · Components" title="Buttons">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <Tile label="default">
              <div className="space-y-3">
                <Button>Default</Button>
                <Button size="sm">Small</Button>
                <Button size="lg">Large</Button>
                <Button disabled>Disabled</Button>
              </div>
            </Tile>
            <Tile label="destructive">
              <div className="space-y-3">
                <Button variant="destructive">Destroy</Button>
                <Button variant="destructive" size="sm">
                  Small
                </Button>
                <Button variant="destructive" size="lg">
                  Large
                </Button>
              </div>
            </Tile>
            <Tile label="outline">
              <div className="space-y-3">
                <Button variant="outline">Outline</Button>
                <Button variant="outline" size="sm">
                  Small
                </Button>
                <Button variant="outline" size="lg">
                  Large
                </Button>
              </div>
            </Tile>
            <Tile label="secondary (highlighter)">
              <div className="space-y-3">
                <Button variant="secondary">Secondary</Button>
                <Button variant="secondary" size="sm">
                  Small
                </Button>
                <Button variant="secondary" size="lg">
                  Large
                </Button>
              </div>
            </Tile>
            <Tile label="ghost">
              <div className="space-y-3">
                <Button variant="ghost">Ghost</Button>
                <Button variant="ghost" size="sm">
                  Small
                </Button>
              </div>
            </Tile>
            <Tile label="link (wavy underline)">
              <div className="space-y-3">
                <Button variant="link">Take action →</Button>
                <Button variant="link" size="sm">
                  Smaller link
                </Button>
              </div>
            </Tile>
          </div>
        </Section>

        {/* ── 5. FORM ELEMENTS ────────────────────────────────── */}
        <Section kicker="05 · Components" title="Form Elements">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div>
                <Label htmlFor="ss-name">Name</Label>
                <Input id="ss-name" placeholder="Maya Johnson" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="ss-email">Email</Label>
                <Input
                  id="ss-email"
                  type="email"
                  defaultValue="maya@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ss-msg">Message</Label>
                <Textarea
                  id="ss-msg"
                  rows={3}
                  placeholder="Tell your story…"
                  defaultValue="Hi Aunt Carla! Thought you might find this helpful before next month. — Maya"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ss-error">Field with error</Label>
                <Input
                  id="ss-error"
                  defaultValue="not-an-email"
                  className="mt-1 border-[#d62828] shadow-[3px_3px_0px_#d62828]"
                />
                <div className="text-xs text-[#d62828] mt-1" style={body}>
                  That doesn&apos;t look like an email.
                </div>
              </div>
              <div>
                <Label>Disabled</Label>
                <Input disabled defaultValue="No edits" className="mt-1" />
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-2" style={mono}>
                  Checkboxes
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="ss-cb-1" defaultChecked />
                    <Label htmlFor="ss-cb-1" className="text-base text-[#1a1a1a]">
                      Mail me before early voting opens
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="ss-cb-2" />
                    <Label htmlFor="ss-cb-2" className="text-base text-[#1a1a1a]">
                      Notify me of future elections
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="ss-cb-3" disabled />
                    <Label htmlFor="ss-cb-3" className="text-base text-[#8a7e6b]">
                      Disabled option
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-2" style={mono}>
                  Inline labels
                </div>
                <div className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-3 shadow-[3px_3px_0px_#1a1a1a]">
                  <div
                    className="text-[10px] uppercase tracking-widest text-[#6b5f4d]"
                    style={mono}
                  >
                    Mailing address
                  </div>
                  <div className="text-base text-[#1a1a1a] mt-1" style={body}>
                    4221 Fern Ave, Sacramento CA 95820
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-[#6b5f4d] mb-2" style={mono}>
                  Helper text
                </div>
                <Label htmlFor="ss-helper">Phone</Label>
                <Input id="ss-helper" placeholder="+1 (916) 555-0100" className="mt-1" />
                <div className="text-xs text-[#6b5f4d] mt-1" style={body}>
                  Optional — only used for election-day SMS reminders.
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* ── 6. CARDS ────────────────────────────────────────── */}
        <Section kicker="06 · Components" title="Cards · CutOuts">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <Card>
              <CardHeader>
                <CardTitle>Standard Card</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={body}>
                  Rectangular paper with a hard ink shadow. The workhorse — use for form sections,
                  order summary blocks, settings panels.
                </p>
              </CardContent>
              <CardFooter>
                <Button>Primary action</Button>
                <Button variant="outline" className="ml-2">
                  Secondary
                </Button>
              </CardFooter>
            </Card>

            <CutOut variant={0} className="p-6">
              <div className="text-xs uppercase tracking-widest text-[#6b5f4d]" style={mono}>
                CutOut · variant 0
              </div>
              <div className="text-2xl text-[#1a1a1a] mt-1" style={display}>
                Scissor-cut paper
              </div>
              <div className="text-base text-[#1a1a1a] mt-2" style={body}>
                For when a rectangle feels too clean. Hand-cut wobble + drop-shadow. Four variants
                with different edge irregularity.
              </div>
            </CutOut>

            <CutOut variant={2} className="p-6">
              <div className="text-xs uppercase tracking-widest text-[#6b5f4d]" style={mono}>
                CutOut · variant 2
              </div>
              <div className="text-2xl text-[#1a1a1a] mt-1" style={display}>
                Dull scissors
              </div>
              <div className="text-base text-[#1a1a1a] mt-2" style={body}>
                More jagged variant. Use sparingly to break visual monotony.
              </div>
            </CutOut>

            <CutOut variant={3} className="p-6" shadow="#d62828">
              <div className="text-xs uppercase tracking-widest text-[#d62828]" style={mono}>
                CutOut · variant 3 · red shadow
              </div>
              <div className="text-2xl text-[#1a1a1a] mt-1" style={display}>
                Loud cut
              </div>
              <div className="text-base text-[#1a1a1a] mt-2" style={body}>
                Drop-shadow color overridable. Combine with marker red for emphasis blocks.
              </div>
            </CutOut>
          </div>
        </Section>

        {/* ── 7. ALERTS ───────────────────────────────────────── */}
        <Section kicker="07 · Components" title="Alerts">
          <div className="space-y-4">
            <Alert>
              <AlertTitle>Heads up</AlertTitle>
              <AlertDescription>
                Default alert. Used for informational messages and quiet announcements.
              </AlertDescription>
            </Alert>

            <Alert variant="destructive">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>
                Destructive alert — wraps the message in red ink and a red drop-shadow. Save this
                for blocking errors that prevent the user from continuing.
              </AlertDescription>
            </Alert>
          </div>
        </Section>

        {/* ── 8. BADGES ───────────────────────────────────────── */}
        <Section kicker="08 · Components" title="Badges">
          <div className="flex flex-wrap items-center gap-3">
            <Badge>Default</Badge>
            <Badge variant="secondary">Highlighter</Badge>
            <Badge variant="destructive">Danger</Badge>
            <Badge variant="outline">Outline (dashed)</Badge>
          </div>
          <div className="text-xs text-[#6b5f4d] mt-3" style={body}>
            Badges always use mono type and uppercase + wide tracking. Pair with hand-lettered body
            for visual contrast.
          </div>
        </Section>

        {/* ── 9. SKELETONS ────────────────────────────────────── */}
        <Section kicker="09 · Components" title="Loading / Skeleton">
          <div className="max-w-md space-y-3">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-10 w-1/3" />
          </div>
          <div className="text-xs text-[#6b5f4d] mt-3" style={body}>
            Skeletons fill space during data fetching. Match the height of the eventual content,
            never push layout around.
          </div>
        </Section>

        {/* ── 10. COMPOSITION EXAMPLES ────────────────────────── */}
        <Section kicker="10 · Patterns" title="In the wild">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Order summary card */}
            <Card>
              <CardHeader>
                <div className="text-xs uppercase tracking-widest text-[#6b5f4d]" style={mono}>
                  Order summary · 3 items
                </div>
                <CardTitle>$9.99 total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm" style={body}>
                  <div className="flex justify-between">
                    <span>Mailers × 4</span>
                    <span>$8.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Donation</span>
                    <span>$5.00</span>
                  </div>
                  <div className="flex justify-between text-[#d62828]">
                    <span>− NEIGHBOR10 (10% off)</span>
                    <span>−$1.20</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-[#1a1a1a] pt-2 mt-2 font-bold">
                    <span>Total</span>
                    <span>$9.99</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full">Place order — $9.99</Button>
              </CardFooter>
            </Card>

            {/* Election card */}
            <CutOut variant={1} className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">Auto-detected</Badge>
                <span className="text-xs text-[#6b5f4d]" style={mono}>
                  Tue, Jun 3, 2026
                </span>
              </div>
              <div className="text-3xl text-[#1a1a1a]" style={display}>
                Sacramento City Council Primary
              </div>
              <div className="text-base text-[#1a1a1a] mt-2" style={body}>
                Sacramento County, CA · Registration deadline May 19 · 6 polling locations near you.
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm">Add a mailer</Button>
                <Button variant="link" size="sm">
                  See ballot →
                </Button>
              </div>
            </CutOut>
          </div>
        </Section>

        {/* ── Footer ─────────────────────────────────────────── */}
        <footer className="border-t-2 border-[#1a1a1a] pt-4 mt-12 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-[#6b5f4d]" style={mono}>
            VotePlan · Style Sample · v1
          </div>
          <div className="text-xs text-[#6b5f4d]" style={body}>
            Marker on posterboard.
          </div>
        </footer>
      </div>
    </div>
  );
}
